<template>
  <div id="view-all-tenancies-component">
    <sticky>
      <div class="clearfix header-panel">
        <div class="header">
          <span class="notice-header">
            {{ $i18n.t( 'tenancy.viewAll.title') }}
          </span>
        </div>
        <div class="right-btns">
          <button
            v-if="canCreateTenancy"
            class="btn btn--orange"
            @click="createNewTenancyRecord()"
          >
            {{ $t('company.tenancyRecord.createTenancyRecord') }}
          </button>
          <HKLButton
            color="orange"
            @click="exportExcel"
          >
            {{ $t('action.exportSearchResults') }}
          </HKLButton>
          <button
            type="button"
            class="btn btn--darkblue"
            @click="() => resetFilters()"
          >
            {{ $t('action.resetFilter') }}
          </button>
          <button
            id="query-btn"
            type="button"
            class="btn btn--orange"
            @click="() => search()"
          >
            {{ $t('action.search') }}
          </button>
        </div>
      </div>
    </sticky>

    <div class="tenancy-view-all-table">
      <div style="display: inline; vertical-align: middle; margin-left: 15px;">
        <span>{{ $t('total') }}: {{ displayListLength }}</span>
      </div>
      <CommonTable
        ref="tenancy-record-table"
        tableId="tenancy-record-table"
        :renderHeaderList="itemHeaders"
        :tableContentLists="sortedItems"
        :needSearch="true"
        :defaultOrder="true"
        :defaultOrderKey="defaultOrderKey"
        :yScrollAuto="true"
        :getDisplayListLength="getDisplayListLength"
        :getConditions="getConditions"
        :enterEvent="search"
        :interactionFunc="interactionFunc"
        :orderMethods="orderMethods"
        :getTableOrder="getTableOrder"
        :orderByDateKeys="['goExpiredDate']"
      />
    </div>
    <HKLRemindModal ref="remindEmptyModal">
      <template slot="title">
        {{ $t('hint.emptySearch') }}
      </template>
    </HKLRemindModal>
  </div>
</template>

<script>
import currencyFormatter from 'currency-formatter'
import lodash from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { ref, getCurrentInstance, reactive, unref, computed } from 'vue-demi'
import CommonTable from '@/controls/CommonTable.vue'
import Sticky from '@/controls/Sticky'
import dateUtil from '@/utils/dateUtil'
import { ccdPermsTypes, aclHelper } from '@/utils/aclHelper'
import { getFloorIndex } from '@/api/buildings/request'
import { getTenancyFloors } from '@/api/tenancies/util'
import { tenancyStatus, tenancyType } from '@/utils/locale'
import { exportAllTenancies } from '@/api/tenancies/request'
import HKLButton from '@/controls/Button/HKLButton'
import { DATA_CACHE_KEYS } from '@/store/util'
import { FEATURE_NAMES } from '@/utils/consts'
import HKLMultiSelect, { ALL_VALUE } from '@/controls/Select/HKLMultiSelect'
import filters from '@/filters/filters'
import HKLRemindModal from '@/controls/Modal/HKLRemindModal.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { INPUT_TYPE } from '@/modules/contacts/searchFilter.const'
import { useStoreGetters } from '@/store/index'
import i18n from '@/utils/i18n'
import { LIST_NAV_KIND } from '@/constants/listNavigation'
import {
  consumeResumeSearchQueryOnce,
  ENGINEER_ALL_TENANCY_RECS_LEAVE_PRESERVE_ROUTE_NAMES,
  VIEW_ALL_RESUME_SEARCH_QUERY,
} from '@/constants/viewAllTableCache'
import viewAllCommonTableSnapshotMixin from '@/mixins/viewAllCommonTableSnapshot.mixin'
import mutationTypes from '@/store/mutation-types'
import { GREEN_OPERATION_SPP_STATUS_OPTIONS } from './AllTenancyRecords.consts'

/**
 * Parse `goStatus` from route query (string or array) into a trimmed non-empty list.
 * @param {string|string[]|undefined|null} raw `?goStatus=` raw value.
 * @returns {string[]}
 * @date 2026-04-10
 * @author Alvis Yip
 */
const normalizeQueryGoStatus = raw => {
  if (raw == null || raw === '') return []
  return (Array.isArray(raw) ? raw : String(raw).split(','))
    .map((v) => String(v).trim())
    .filter((v) => v !== '')
}

/**
 * True when the premise row’s end date is in the past (stale lease line).
 * @param {{ endDate?: string }} row Premises detail row.
 * @returns {boolean}
 * @date 2026-04-10
 * @author Alvis Yip
 */
const isPremiseExpired = (row) => !!(row && row.endDate && !dateUtil.isBeforeEndDate(row.endDate))

/**
 * Escape text for safe HTML insertion.
 * @param {unknown} str Raw cell text.
 * @returns {string}
 * @date 2026-04-10
 * @author Alvis Yip
 */
const escapeHtml = (str) => lodash.escape(String(str == null ? '' : str))

/**
 * Wrap plain text in a grey span when the premise is expired (view-all grid display).
 * @param {unknown} rawText Unescaped label text.
 * @param {boolean} isExpired Whether this segment is expired.
 * @returns {string} Safe HTML string.
 * @date 2026-04-10
 * @author Alvis Yip
 */
const wrapExpiredIf = (rawText, isExpired) => {
  const text = escapeHtml(rawText)
  return isExpired
    ? `<span class="tenancy-record-expired">${text}</span>`
    : text
}

/**
 * Build comma-separated floor cell HTML with per-floor expiry styling.
 * @param {Array<{ floor?: string, endDate?: string }>} [premisesDetailList] Premises rows for the tenancy.
 * @param {string[]} [floorLabels] Ordered unique floor labels.
 * @returns {string}
 * @date 2026-04-10
 * @author Alvis Yip
 */
const buildFloorSegmentsHtml = (premisesDetailList = [], floorLabels = []) => {
  if (!floorLabels.length) return ''
  return floorLabels.map((floor) => {
    const rowsForFloor = premisesDetailList.filter((o) => o.floor === floor)
    const allExpired = rowsForFloor.length > 0 && rowsForFloor.every((o) => isPremiseExpired(o))
    return wrapExpiredIf(floor, allExpired)
  }).join(', ')
}

/**
 * Build premises cell HTML (`||`-joined) with per-row expiry styling.
 * @param {Array<{ detail?: string, endDate?: string }>} [premisesDetailList] Premises rows.
 * @returns {string}
 * @date 2026-04-10
 * @author Alvis Yip
 */
const buildPremisesSegmentsHtml = (premisesDetailList = []) => {
  if (!premisesDetailList.length) return ''
  return premisesDetailList.map((o) => wrapExpiredIf(o.detail, isPremiseExpired(o))).join(' || ')
}

const sortingAttributes = {
  tenancy: {
    type: null,
    status: [tenancyStatus.ACTIVE],
    premisesDetailList: null,
    premises: null,
    tradeName: null,
  },
  building: {
    code: null,
  },
  company: {
    name: null,
  },
  'building.site': {
    code: null,
  },
  greenOperation: {
    sppReviewStatus: [],
    sppExpiredDate: {
      gte: null,
      lte: null,
    },
  },
}

export default {
  name: 'DashboardAllTenancyRecords',
  /** @see `@/mixins/viewAllCommonTableSnapshot.mixin.js` */
  viewAllTableSnapshotId: 'tenancy-record-table',
  viewAllTableLeavePreserveRouteNames: ENGINEER_ALL_TENANCY_RECS_LEAVE_PRESERVE_ROUTE_NAMES,
  viewAllTableOnNonPreserveLeave () {
    this.$store.dispatch('listNavigation/clearListNavigationSequence')
    this.$store.dispatch('listNavigation/clearSuspendedTenancyRecord')
  },
  components: {
    Sticky,
    CommonTable,
    HKLButton,
    HKLRemindModal,
    HKLMultiSelect,
  },
  mixins: [viewAllCommonTableSnapshotMixin],
  setup () {
    const { proxy: that } = getCurrentInstance()
    let returnData = {}
    const defaultOrderKey = 'companyName'
    const diskCacheKey = DATA_CACHE_KEYS.VIEW_ALL_TR
    const orderKey = ref('companyName')
    const reverse = ref(true)
    const getTableOrder = (key, order) => {
      orderKey.value = key
      reverse.value = order
    }
    const exportExcel = async () => {
      await exportAllTenancies({
        diskCacheKey,
        orderInfo: {
          filterFloors: lodash.get(that.sortingAttributes, 'tenancy.premisesDetailList'),
          orderbyAttr: orderKey.value,
          reverse: Number(reverse.value),
        },
      })
    }

    const defaultFilterValues = reactive({
      greenOperationStatus: [],
      tenancyStatus: [tenancyStatus.ACTIVE],
    })
    returnData = Object.assign(returnData, {
      exportExcel,
      getTableOrder,
      defaultOrderKey,
      diskCacheKey,
      defaultFilterValues,
    })
    const checkIsEmptyFilter = () => {
      const obj = lodash.fromPairs(
        that.itemHeaders.map((itemHeader) => [itemHeader.name, lodash.get(that.sortingAttributes, itemHeader.queryName)]),
      )
      that.itemHeaders.forEach((itemHeader) => {
        if (itemHeader.inputType !== INPUT_TYPE.DATE) return
        const key = itemHeader.name
        lodash.set(obj, key, lodash.get(obj, `${key}.gte`) || lodash.get(obj, `${key}.lte`))
      })

      const result = Object.values(obj).some((v) => {
        if (Array.isArray(v) || typeof v === 'string') return !!v.length
        return !!v
      })
      return !result
    }
    const { open: remindEmptyModalOpen, modal: remindEmptyModal } = useRefModal()
    const { tenancyTypes, isTrRep } = useStoreGetters('user', ['tenancyTypes', 'isTrRep'])
    const typeOptions = computed(() => {
      const result = unref(isTrRep) ? Object.values(tenancyType) : unref(tenancyTypes)
      return result.map(type => {
        return { name: i18n.t(`tenancy.${lodash.toLower(type)}`), value: type }
      })
    })
    returnData = Object.assign(returnData, {
      checkIsEmptyFilter,
      remindEmptyModalOpen,
      remindEmptyModal,
      typeOptions,
    })
    return returnData
  },
  data () {
    return {
      currencyFormatter,
      emptyValue: this.$t('emptyValue'),
      sortingAttributes: lodash.cloneDeep(sortingAttributes),
      orderbyAttr: 'companyName',
      reverse: false,
      commonTableSearch: '',
      interactionFunc: {
        'companyName': this.navToPage,
      },
      orderMethods: {
        floor: this.orderByFloor,
      },
      statusOptions: [
        {
          name: filters.customStatusLabel(tenancyStatus.ACTIVE).toUpperCase(),
          value: tenancyStatus.ACTIVE,
        },
        {
          name: filters.customStatusLabel(tenancyStatus.INACTIVE).toUpperCase(),
          value: tenancyStatus.INACTIVE,
        }],
      greenOperationStatus: GREEN_OPERATION_SPP_STATUS_OPTIONS.slice(1)
        .map((option) => ({ name: filters.customStatusLabel(option, 'label').toUpperCase(), value: option.val })),
      /** Latest grid rows from View All API (not vuex — same pattern as `projectRows` on Project ViewAll). */
      tenancyRecordRows: [],
      displayListLength: 0,
    }
  },
  computed: {
    ...mapGetters('tenancyRecords', ['lastSearchedAttributes']),
    ...mapGetters('regions', ['featureStatus']),
    parsedTenancyRecords () {
      return this.tenancyRecordRows.map((rec) => {
        const building = deep(rec, 'building.code')
        const allFloors = getTenancyFloors(rec, false)
        const floors = lodash.sortBy(allFloors, item => {
          return getFloorIndex(building, item)
        })
        const premisesDetailList = lodash.get(rec, 'premisesDetailList', []) || []
        const floorLabels = lodash.uniq(floors)
        const floorHtml = buildFloorSegmentsHtml(premisesDetailList, floorLabels)
        const premisesHtml = buildPremisesSegmentsHtml(premisesDetailList)

        return {
          id: rec.id,
          companyName: deep(rec, 'company.name'),
          tradeName: rec.tradeName,
          building,
          floor: floorHtml,
          floorArray: floors,
          floorIndex: getFloorIndex(building, floors[0]),
          premises: premisesHtml || null,
          status: rec.status,
          type: rec.type,
          allowLink: true,
          goStatus: lodash.get(rec, 'greenOperation.sppReviewStatus'),
          goExpiredDate: lodash.get(rec, 'greenOperation.sppExpiredDate'),
        }
      })
    },
    sortedItems () {
      const result = lodash.orderBy(this.parsedTenancyRecords, this.orderbyAttr === 'floor' ? (item) => { return item.floorIndex } : this.orderbyAttr)
      return this.reverse ? result.reverse() : result
    },
    canCreateTenancy () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.create)
    },
    itemHeaders () {
      return [
        {
          attrPath: 'companyName',
          title: this.$t('tenancy.companyName'),
          name: 'companyName',
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.emptyValue,
          queryName: ['company', 'name'],
        },
        {
          attrPath: 'tradeName',
          title: this.$t('tenancy.tradeName'),
          name: 'tradeName',
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.emptyValue,
          queryName: ['tenancy', 'tradeName'],
        },
        {
          attrPath: 'building',
          title: this.$t('tenancy.building'),
          name: 'building',
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.emptyValue,
          queryName: ['building', 'code'],
        },
        {
          attrPath: 'floor',
          title: this.$t('tenancy.floor'),
          inputType: INPUT_TYPE.INPUT,
          name: 'floor',
          defaultValue: this.emptyValue,
          queryName: ['tenancy', 'premisesDetailList'],
        },
        {
          attrPath: 'premises',
          title: this.$t('tenancy.premises'),
          inputType: INPUT_TYPE.INPUT,
          name: 'premises',
          defaultValue: this.emptyValue,
          queryName: ['tenancy', 'premises'],
        },
        {
          attrPath: 'type',
          title: this.$t('tenancy.tenancyType'),
          name: 'type',
          component: HKLMultiSelect,
          defaultFilterValue: [],
          options: {
            attributes: {
              includeAll: this.typeOptions.length > 1,
              options: this.typeOptions,
              placeholder: '',
            },
          },
          defaultValue: this.emptyValue,
          queryName: ['tenancy', 'type'],
          formatRule: (val) => this.$t(`tenancy.${lodash.toLower(val)}`),
        },
        this.featureStatus[FEATURE_NAMES.GREEN_OPERATION] && {
          attrPath: 'goStatus',
          title: this.$t('table.header.greenOperationStatus'),
          name: 'goStatus',
          searchable: true,
          component: HKLMultiSelect,
          defaultFilterValue: this.defaultFilterValues.greenOperationStatus,
          options: {
            attributes: {
              includeAll: true,
              options: this.greenOperationStatus,
              placeholder: '',
            },
          },
          defaultValue: this.emptyValue,
          queryName: ['greenOperation', 'sppReviewStatus'],
          formatRule: (val) => this.$options.filters.customStatusLabel(val).toUpperCase(),
        },
        this.featureStatus[FEATURE_NAMES.GREEN_OPERATION] && {
          attrPath: 'goExpiredDate',
          title: this.$t('tenancy.greenOperation.expireOn'),
          name: 'goExpiredDate',
          inputType: INPUT_TYPE.DATE,
          singleDatePicker: true,
          datePickerCanPutTotop: false,
          disabledDate: false,
          dateFromPlaceholder: '',
          queryName: ['greenOperation', 'sppExpiredDate'],
          formatRule: val => this.formatDate(val),
        },
        {
          attrPath: 'status',
          title: this.$t('tenancyProject.status'),
          name: 'status',
          defaultValue: this.emptyValue,
          defaultFilterValue: this.defaultFilterValues.tenancyStatus,
          component: HKLMultiSelect,
          options: {
            attributes: {
              includeAll: true,
              options: this.statusOptions,
              placeholder: '',
            },
          },
          queryName: ['tenancy', 'status'],
          formatRule: (val) => this.$options.filters.customStatusLabel(val).toUpperCase(),
        },
      ].filter(Boolean)
    },
  },
  /** HKL resumeSearch: clear grid rows locally; keep `tables` filter/order blob. Auto-refetch in `mounted` only when vuex snapshot exists. */
  created () {
    const q = this.$route.query || {}
    if (String(q[VIEW_ALL_RESUME_SEARCH_QUERY]) !== '1') return
    this.tenancyRecordRows = []
  },
  async mounted () {
    $('#search-panel').keydown(function (event) {
      var keypressed = event.keyCode || event.which
      if (keypressed === 13) {
        $('#query-btn').click()
      }
    })
    let shouldSearch = this.applyGreenOperationFiltersFromRouteQuery()

    const { didStripResumeSearch } = await consumeResumeSearchQueryOnce(
      this.$router,
      this.$route.query,
      'allTenancyRecs',
    )
    const lastSearchAttributes = this.lastSearchedAttributes
    const hasPersistedSearchSnapshot = !lodash.isEmpty(lastSearchAttributes)
    if (didStripResumeSearch && hasPersistedSearchSnapshot) {
      this.sortingAttributes = lodash.cloneDeep(lastSearchAttributes)
      shouldSearch = true
    }

    await this.$nextTick()
    this.syncTableFilterConditionsFromSortingAttributes()

    if (shouldSearch) {
      await this.queryTenancyRecords({ skipDiskCache: didStripResumeSearch })
    }

    $('.tenancy-view-all-table-content-container').css('width', $('.tenancy-view-all-table-header-container tr').width())
  },
  methods: {
    ...mapActions('tenancyRecords', ['loadAllTenancies', 'setLastSearchedAttributes']),
    getTenancyRecordNavigationEntries () {
      const table = this.$refs['tenancy-record-table']
      const rows = table && Array.isArray(table.formatList) ? table.formatList : this.sortedItems
      if (!rows || !rows.length) return []
      return rows
        .filter(row => row.allowLink !== false)
        .map(row => ({ id: row.id, kind: LIST_NAV_KIND.TENANCY_RECORD }))
    },
    commitTenancyListNavigationSequence () {
      this.$store.dispatch('listNavigation/clearSuspendedTenancyRecord')
      const entries = this.getTenancyRecordNavigationEntries()
      this.$store.commit(`listNavigation/${mutationTypes.LIST_NAVIGATION_SET_SEQUENCE}`, { entries })
    },
    navToPage (item) {
      this.commitTenancyListNavigationSequence()
      this.$router.push({
        name: 'ccdTenancyView',
        params: { id: item.id },
      })
    },
    formatDate (item) {
      return item ? dateUtil.formatDate(item) : this.emptyValue
    },
    /**
     * Apply `?goStatus=` to GO filters. When matched, clears tenancy status so the search is GO-driven only (no default ACTIVE).
     * @returns {boolean} True when GO filters were applied (caller may auto-search).
     */
    applyGreenOperationFiltersFromRouteQuery () {
      const goStatusList = normalizeQueryGoStatus(this.$route.query.goStatus)
      if (!goStatusList.length) return false
      const goStatusFilterData = this.greenOperationStatus
        .filter((option) => goStatusList.includes(option.value))
        .map((option) => option.value)
      if (!goStatusFilterData.length) return false
      this.$set(this.sortingAttributes.greenOperation, 'sppReviewStatus', goStatusFilterData)
      this.$set(this.defaultFilterValues, 'greenOperationStatus', goStatusFilterData)
      this.$set(this.sortingAttributes.tenancy, 'status', null)
      this.defaultFilterValues.tenancyStatus = []
      return true
    },
    createNewTenancyRecord () {
      this.$router.push({
        name: 'ccdTenancyCreate',
      })
    },
    sortBy (attributePath) {
      if (this.orderbyAttr === attributePath) {
        this.reverse = !this.reverse
      }
      this.orderbyAttr = attributePath
    },
    async resetFilters () {
      this.sortingAttributes = lodash.cloneDeep(sortingAttributes)
      this.sortingAttributes.tenancy.status = null
      this.defaultFilterValues.greenOperationStatus = []
      this.defaultFilterValues.tenancyStatus = []
      this.$store.dispatch('listNavigation/clearListNavigationSequence')
      this.$store.dispatch('listNavigation/clearSuspendedTenancyRecord')
      await this.setLastSearchedAttributes({})
      await this.$nextTick()
      this.syncTableFilterConditionsFromSortingAttributes()
      const table = this.$refs['tenancy-record-table']
      if (table) table.resetFilterUiOnly()
    },
    navBack () {
      this.$router.goBack()
    },
    async search () {
      const table = this.$refs['tenancy-record-table']
      if (table && table.filterConditions) {
        this.applyFilterConditionsToSortingAttributes(table.filterConditions)
      }
      if (this.checkIsEmptyFilter()) return this.remindEmptyModalOpen()
      await this.queryTenancyRecords()
    },
    async queryTenancyRecords ({ skipDiskCache = false } = {}) {
      const rows = await this.loadAllTenancies({
        query: { ...this.getParsedAttributes() },
        diskCacheKey: skipDiskCache ? null : this.diskCacheKey,
      })
      this.tenancyRecordRows = Array.isArray(rows) ? rows : []
      await this.setLastSearchedAttributes(lodash.cloneDeep(this.sortingAttributes))
    },
    getDisplayListLength (length) {
      this.displayListLength = length
    },
    getParsedAttributes () {
      let sortingAttributes = lodash.cloneDeep(this.sortingAttributes)
      this.itemHeaders.forEach(header => {
        if (header.inputType !== INPUT_TYPE.INPUT) return
        const value = lodash.get(sortingAttributes, header.queryName)
        if (typeof value === 'string') {
          lodash.set(sortingAttributes, header.queryName, value.trim())
        }
      })
      sortingAttributes.tenancy.status = lodash.get(sortingAttributes, 'tenancy.status')
      sortingAttributes.tenancy.type = lodash.get(sortingAttributes, 'tenancy.type')
      sortingAttributes.greenOperation.sppReviewStatus = normalizeQueryGoStatus(
        lodash.get(sortingAttributes, 'greenOperation.sppReviewStatus'),
      )
      const expiredGte = lodash.get(sortingAttributes, 'greenOperation.sppExpiredDate.gte')
      if (expiredGte) {
        sortingAttributes.greenOperation.sppExpiredDate.lte = dateUtil.getEndTimeOfDay(this.sortingAttributes.greenOperation.sppExpiredDate.gte)
      } else {
        sortingAttributes = lodash.omit(sortingAttributes, ['greenOperation.sppExpiredDate'])
      }
      const multiSelectAttrs = ['status', 'type']
      multiSelectAttrs.forEach(attr => {
        if (sortingAttributes.tenancy[attr] && (sortingAttributes.tenancy[attr].includes(ALL_VALUE) || !sortingAttributes.tenancy[attr].length)) {
          delete sortingAttributes.tenancy[attr]
        }
      })
      if (sortingAttributes.greenOperation.sppReviewStatus && (sortingAttributes.greenOperation.sppReviewStatus.includes(ALL_VALUE) || !sortingAttributes.greenOperation.sppReviewStatus.length)) {
        delete sortingAttributes.greenOperation.sppReviewStatus
      }

      const floor = lodash.get(sortingAttributes, 'tenancy.premisesDetailList')
      if (floor && floor !== null) {
        if (floor === this.emptyValue) {
          sortingAttributes.tenancy.premisesDetailList = null
        } else {
          sortingAttributes.tenancy.premisesDetailList = `"floor":"${floor}`
        }
      }
      return sortingAttributes
    },
    applyFilterConditionsToSortingAttributes (rawConditions) {
      this.searchConditionsConvertToQuery(lodash.cloneDeep(rawConditions || {}))
    },
    syncTableFilterConditionsFromSortingAttributes () {
      const table = this.$refs['tenancy-record-table']
      if (!table) return
      const conditions = {}
      const setInputFilter = (name, value) => {
        if (value === undefined || value === null || value === '') return
        conditions[name] = { value, inputType: INPUT_TYPE.INPUT }
      }
      const setMultiFilter = (name, value) => {
        if (value === undefined || value === null) return
        conditions[name] = {
          value: Array.isArray(value) ? value : [],
          inputType: INPUT_TYPE.COMPONENT,
        }
      }
      setInputFilter('companyName', lodash.get(this.sortingAttributes, 'company.name'))
      setInputFilter('tradeName', lodash.get(this.sortingAttributes, 'tenancy.tradeName'))
      setInputFilter('building', lodash.get(this.sortingAttributes, 'building.code'))
      setInputFilter('floor', lodash.get(this.sortingAttributes, 'tenancy.premisesDetailList'))
      setInputFilter('premises', lodash.get(this.sortingAttributes, 'tenancy.premises'))
      setMultiFilter('type', lodash.get(this.sortingAttributes, 'tenancy.type'))
      setMultiFilter('status', lodash.get(this.sortingAttributes, 'tenancy.status'))
      setMultiFilter('goStatus', lodash.get(this.sortingAttributes, 'greenOperation.sppReviewStatus'))
      const expiredGte = lodash.get(this.sortingAttributes, 'greenOperation.sppExpiredDate.gte')
      if (expiredGte) {
        conditions.goExpiredDate = {
          value: [expiredGte, null],
          inputType: INPUT_TYPE.DATE,
        }
      }
      const managedKeys = [
        'companyName',
        'tradeName',
        'building',
        'floor',
        'premises',
        'type',
        'status',
        'goStatus',
        'goExpiredDate',
      ]
      const next = lodash.cloneDeep(table.filterConditions || {})
      managedKeys.forEach((key) => {
        delete next[key]
      })
      table.filterConditions = Object.assign(next, conditions)
    },
    getConditions (conditions) {
      this.applyFilterConditionsToSortingAttributes(conditions)
      this.$nextTick(() => this.syncTableFilterConditionsFromSortingAttributes())
    },
    searchConditionsConvertToQuery (conditions) {
      this.itemHeaders.map(header => {
        const condition = conditions[header.name]
        if (condition) {
          const value = condition.value
          switch (header.name) {
            case 'goExpiredDate' :
              this.sortingAttributes[header.queryName[0]][header.queryName[1]].gte = value[0] || null
              break
            default:
              this.sortingAttributes[header.queryName[0]][header.queryName[1]] = value
              break
          }
        }
      })
    },
    orderByFloor (item, order, orderKey) {
      return item.floorIndex
    },
  },
}
</script>

<style lang="scss" scoped src="styles/viewAll.scss"></style>
<style lang="scss" scoped src="styles/dashboard/style.scss"></style>
<style lang="scss" scoped src="styles/dashboard/datatable.scss"></style>
<style scoped lang="scss">
.tenancy-view-all-table {
  height: calc(100vh - 196px);
}
</style>

<style lang="scss">
#view-all-tenancies-component {
  .nav-bar {
    max-width: 100%;
    margin: 0;
  }

  .inline-date-picker-block .vdp-datepicker {
    width: 120px!important;
    display: inline-block;
    min-height: 34px;
    vertical-align: bottom;
    input[type="text"] {
      margin: 0px;
    }
  }

  .premise-header {
    max-width: 600px;
    overflow: hidden;
  }

  .tenancy-record-expired {
    color: #c6c6c6;
  }
}

$COLUMN_WIDTH_COMPANY_NAME: 300px;
$COLUMN_WIDTH_TRADE_NAME: 300px;
$COLUMN_WIDTH_BUILDING: 130px;
$COLUMN_WIDTH_FLOOR: 300px;
$COLUMN_WIDTH_PREMISES: 300px;
$COLUMN_WIDTH_TYPE: 200px;
$COLUMN_WIDTH_STATUS: 200px;
$COLUMN_WIDTH_BELOW: $COLUMN_WIDTH_COMPANY_NAME
  + $COLUMN_WIDTH_TRADE_NAME
  + $COLUMN_WIDTH_BUILDING
  + $COLUMN_WIDTH_FLOOR
  + $COLUMN_WIDTH_TYPE
  + $COLUMN_WIDTH_STATUS;
$TABLE_MIN_WIDTH: $COLUMN_WIDTH_BELOW + $COLUMN_WIDTH_PREMISES + 10px;
$COLUMN_WIDTH_GREEN_OPERATION_STATUS: 280px;
$COLUMN_WIDTH_GREEN_OPERATION_EXPIRE: 200px;

.tenancy-record-table_common-table {
  min-height: unset !important;
  height: calc(100% - 36px);
  .companyName {
    width: $COLUMN_WIDTH_COMPANY_NAME;
  }
  .tradeName {
    width: $COLUMN_WIDTH_TRADE_NAME;
  }
  .building {
    width: $COLUMN_WIDTH_BUILDING;
  }
  .floor {
    width: $COLUMN_WIDTH_FLOOR;
  }
  .premises {
    min-width: $COLUMN_WIDTH_PREMISES;
    width: calc(100% - #{$COLUMN_WIDTH_BELOW});
  }
  .type {
    width: $COLUMN_WIDTH_TYPE;
  }
  .goStatus {
    width: $COLUMN_WIDTH_GREEN_OPERATION_STATUS;
  }
  .goExpiredDate {
    width: $COLUMN_WIDTH_GREEN_OPERATION_EXPIRE;
  }
  .status {
    width: $COLUMN_WIDTH_STATUS;
  }

  .common-table-header-container {
    min-width: $TABLE_MIN_WIDTH !important;
  }
  &:has(.goStatus) {
    .premises {
      width: calc(100% - #{$COLUMN_WIDTH_BELOW + $COLUMN_WIDTH_GREEN_OPERATION_STATUS + $COLUMN_WIDTH_GREEN_OPERATION_EXPIRE});
    }
    .common-table-header-container {
      min-width: ($TABLE_MIN_WIDTH + $COLUMN_WIDTH_GREEN_OPERATION_STATUS + $COLUMN_WIDTH_GREEN_OPERATION_EXPIRE) !important;
    }
  }
}
</style>
